import type { GameMode } from './game';
import { haversineKm, type LatLng } from './geo';
import { continentOf } from './world';

/** Points awarded for a perfect guess. */
export const MAX_ROUND_POINTS = 5000;

/** Guesses closer than this are always perfect (Street View coordinates are not that precise). */
export const PERFECT_RADIUS_KM = 0.05;

/**
 * Points halve every `regionScaleKm / HALVING_DIVISOR` kilometers: ~1,430 km on the world map,
 * ~80 km on a country-sized map like France. Equivalently, points = max × e^(-9.7 × d / scale).
 */
const HALVING_DIVISOR = 14;

export function scoreGuess(distanceKm: number, regionScaleKm: number): number {
  if (!Number.isFinite(distanceKm) || distanceKm < 0) return 0;
  if (distanceKm <= PERFECT_RADIUS_KM) return MAX_ROUND_POINTS;
  const halvingDistanceKm = regionScaleKm / HALVING_DIVISOR;
  return Math.round(MAX_ROUND_POINTS * 0.5 ** (distanceKm / halvingDistanceKm));
}

/** Party option: up to this share of a round's points comes on top for locking in early. */
export const SPEED_BONUS_SHARE = 0.25;
/** Without a time limit, the speed bonus runs out after this long. */
export const SPEED_BONUS_WINDOW_MS = 60_000;

/**
 * Extra points for a quick guess: the full share when instant, nothing once `windowMs` has
 * passed. In proportion to the score, so a wild early click earns next to nothing.
 */
export function speedBonus(points: number, elapsedMs: number, windowMs: number): number {
  const speed = Math.min(1, Math.max(0, 1 - elapsedMs / windowMs));
  return Math.round(points * SPEED_BONUS_SHARE * speed);
}

/** Country mode: full points for the right country, nothing otherwise. */
export function scoreCountryGuess(guessCountry: string | null, answerCountry: string): number {
  return guessCountry === answerCountry ? MAX_ROUND_POINTS : 0;
}

/** Combo mode: a round's points come from three layers. */
export const COMBO_CONTINENT_POINTS = 1000;
export const COMBO_COUNTRY_POINTS = 1000;
export const COMBO_PROXIMITY_POINTS = MAX_ROUND_POINTS - COMBO_CONTINENT_POINTS - COMBO_COUNTRY_POINTS;

export interface ComboBreakdown {
  continent: number;
  country: number;
  proximity: number;
}

/**
 * Combo mode: the right continent, then the right country, then closeness (the pin mode
 * curve, scaled to its share). A pin in the sea gets closeness only.
 */
export function comboBreakdown(
  guessCountry: string | null,
  answerCountry: string,
  distanceKm: number,
  regionScaleKm: number,
): ComboBreakdown {
  const sameContinent = guessCountry !== null && continentOf(guessCountry) === continentOf(answerCountry);
  return {
    continent: sameContinent ? COMBO_CONTINENT_POINTS : 0,
    country: guessCountry === answerCountry ? COMBO_COUNTRY_POINTS : 0,
    proximity: Math.round((scoreGuess(distanceKm, regionScaleKm) * COMBO_PROXIMITY_POINTS) / MAX_ROUND_POINTS),
  };
}

export function maxGamePoints(rounds: number): number {
  return rounds * MAX_ROUND_POINTS;
}

export interface RoundScore {
  distanceKm: number | null;
  points: number;
  /** Country and combo modes: the country the guess was in. */
  guessCountry: string | null;
}

/**
 * Scores a guess under the game's mode. `countryAt` tells which country a point is in (only
 * needed in country and combo modes).
 */
export function scoreRound(
  mode: GameMode,
  scaleKm: number,
  guess: LatLng | null,
  answer: LatLng & { countryCode: string },
  countryAt: (point: LatLng) => string | null,
): RoundScore {
  if (!guess) return { distanceKm: null, points: 0, guessCountry: null };
  const distanceKm = haversineKm(guess, answer);
  if (mode === 'pin') return { distanceKm, points: scoreGuess(distanceKm, scaleKm), guessCountry: null };
  const guessCountry = countryAt(guess);
  if (mode === 'country') return { distanceKm, points: scoreCountryGuess(guessCountry, answer.countryCode), guessCountry };
  const parts = comboBreakdown(guessCountry, answer.countryCode, distanceKm, scaleKm);
  return { distanceKm, points: parts.continent + parts.country + parts.proximity, guessCountry };
}
